import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useHistory } from 'react-router';
import { useMutation } from '@apollo/client';

import { AlertContext } from 'lib/alert-context/alert-context';

import ActionSheet, { Action } from 'four-nets/ui-v2/action-sheet/action-sheet';
import ComplainDialog from 'four-nets/ui-v2/complain-dialog/complain-dialog';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import useWriteImagesCache from 'four-nets/photoblog/posts/album/use-write-images-cache/use-write-images-cache';
import { FIRST } from 'four-nets/photoblog/posts/album/detail/detail-loader';

import removeImageMutation, {
    RemoveImageMutation,
    RemoveImageMutationVariables,
} from '../mutations/remove-image.graphql';
import { ImageNode } from '../feed-images';
import ImageTitleModal from '../description/image-title/image-title-modal';

import complainMutation, { ComplainMutation, ComplainMutationVariables } from './mutations/complain.graphql';
import subscribeMutation, { SubscribeMutation, SubscribeMutationVariables } from './mutations/subscribe.graphql';
import unsubscribeMutation, {
    UnsubscribeMutation,
    UnsubscribeMutationVariables,
} from './mutations/unsubscribe.graphql';

export interface Props {
    onClose: () => void;
    open: boolean;
    node: NonNullable<ImageNode>;
    photoswipe?: any;
}

const More: React.FC<Props> = props => {
    const { onClose, photoswipe, open, node } = props;

    const { acknowledge, confirm, retry } = useContext(AlertContext);
    const { viewer } = useContext(ViewerContext);

    const location = useLocation();
    const history = useHistory();
    const writeImagesCache = useWriteImagesCache();

    const [imageTitleModalOpen, setImageTitleModalOpen] = useState(false);

    const [complain] = useMutation<ComplainMutation, ComplainMutationVariables>(complainMutation);
    const [unsubscribe] = useMutation<UnsubscribeMutation, UnsubscribeMutationVariables>(unsubscribeMutation, {
        variables: { id: node.id },
    });
    const [subscribe] = useMutation<SubscribeMutation, SubscribeMutationVariables>(subscribeMutation, {
        variables: { id: node.id },
    });
    const [removeImage] = useMutation<RemoveImageMutation, RemoveImageMutationVariables>(removeImageMutation);

    const onUnsubscribeAccept = () => {
        return _onAccept(unsubscribe, <FormattedMessage id='FORUM_THREAD_UNSUBSCRIBED' />);
    };

    const onSubscribeAccept = () => {
        return _onAccept(subscribe, <FormattedMessage id='FORUM_THREAD_SUBSCRIBED' />);
    };

    const _onAccept = (fn: (options?: any) => Promise<any>, body: React.ReactNode) => {
        const acceptFn = () => {
            fn()
                .then(() => {
                    acknowledge({
                        body,
                        onAccept: () => {},
                    });
                })
                .catch(error => {
                    retry({
                        body: error.message,
                        onAccept: acceptFn,
                    });
                });
        };
        onClose();
        acceptFn();
    };

    const onImageRemoveAccept = () => {
        removeImage({ variables: { id: node.id } })
            .then(result => {
                if (result.data?.removeImage?.post?.images?.edges.length) {
                    writeImagesCache(result.data.removeImage.post.id, result.data.removeImage.post.images.edges);
                }
            })
            .then(() => {
                if (photoswipe) {
                    removeNodeFromPhotoswipeImages(node);
                }

                acknowledge({
                    body: <FormattedMessage id='IMAGE_WAS_DELETED' />,
                    onAccept: () => {
                        if (locatedInImageDetailPage()) {
                            redirectToParentPost();
                        }
                    },
                });
            })
            .catch(error => {
                retry({
                    body: error.message,
                    onAccept: onImageRemoveAccept,
                });
            });
    };

    const removeNodeFromPhotoswipeImages = (node: any) => {
        if (photoswipe.items.length === 1) {
            photoswipe.close();
        } else {
            let index;

            const { items } = photoswipe;

            for (let i = 0; i < items.length; i++) {
                if ((items[i] as any).id === (node && node.id)) {
                    index = i;
                    break;
                }
            }

            if (index !== undefined) {
                if (index === items.length - 1) {
                    photoswipe.prev();
                }

                items.splice(index, 1);
                photoswipe.invalidateCurrItems();
                photoswipe.updateSize(true);
                (photoswipe.ui as any).update();
            } else {
                photoswipe.close();
            }
        }
    };

    const locatedInImageDetailPage = () => {
        return Boolean(!photoswipe);
    };

    const redirectToParentPost = () => {
        const path = location.pathname;
        const parts = path.split('/').filter(item => item !== '');
        parts.pop();
        const newPath = `/${parts.join('/')}`;
        history.push({
            pathname: newPath,
        });
    };

    return (
        <>
            <ComplainDialog>
                {({ complain: openComplain }) => (
                    <ActionSheet open={open} onClose={onClose}>
                        {({ close }) => (
                            <React.Fragment>
                                {viewer ? (
                                    node.isSubscribedByViewer ? (
                                        <Action
                                            onClick={() => {
                                                onUnsubscribeAccept();
                                            }}
                                        >
                                            <FormattedMessage id='DISABLE_NOTIFICATIONS' />
                                        </Action>
                                    ) : (
                                        <Action
                                            onClick={() => {
                                                onSubscribeAccept();
                                            }}
                                        >
                                            <FormattedMessage id='ENABLE_NOTIFICATIONS' />
                                        </Action>
                                    )
                                ) : null}
                                {viewer ? (
                                    <Action
                                        onClick={() => {
                                            onClose();
                                            openComplain({
                                                title: <FormattedMessage id='COMPLAIN_IMAGE' />,
                                                onSubmit: ({ comment }) =>
                                                    complain({
                                                        variables: {
                                                            id: node.id,
                                                            comment,
                                                        },
                                                    }),
                                            });
                                        }}
                                    >
                                        <FormattedMessage id='REPORT' />
                                    </Action>
                                ) : null}
                                {node.isCreatedByViewer ? (
                                    <>
                                        <Action
                                            onClick={() => {
                                                onClose();
                                                setImageTitleModalOpen(true);
                                            }}
                                        >
                                            <FormattedMessage id='EDIT_IMAGE_TITLE' />
                                        </Action>
                                        <Action
                                            onClick={() => {
                                                onClose();
                                                confirm({
                                                    body: <FormattedMessage id='CONFIRM_IMAGE_DELETE' />,
                                                    onAccept: onImageRemoveAccept,
                                                    acceptButtonType: 'warn',
                                                });
                                            }}
                                        >
                                            <FormattedMessage id='BUTTON_LABEL_DELETE' />
                                        </Action>
                                    </>
                                ) : null}

                                {close}
                            </React.Fragment>
                        )}
                    </ActionSheet>
                )}
            </ComplainDialog>
            <ImageTitleModal
                open={imageTitleModalOpen}
                id={node.id}
                title={node.title}
                close={() => setImageTitleModalOpen(false)}
            />
        </>
    );
};

export default More;
